<!-- @format -->

<template>
    <div class="gst-price-details d-flex flex-column">
        <div
            v-if="isManageBookingFlow"
            class="gst-price-details__prices">
            <div class="gst-price-details__price">
                +{{ roundedUpBasePriceFormatted }} {{ $t('total') }}
            </div>
        </div>
        <div
            v-else
            class="gst-price-details__prices"
            :class="{ 'gst-price-details__prices--all-inclusive': allInclusivePricing }">
            <div
                v-if="giftCard || showDiscount"
                class="gst-price-details__gifts d-flex flex-wrap mb-1">
                <SaveAmountCardChip
                    v-if="showDiscount"
                    :amount="discountedAmount"
                    :currency="currency" />
                <GiftCardChip
                    v-if="giftCard"
                    :amount="giftCard.amount"
                    :currency="giftCard.currency"
                    :up-to-label="hasMaxGiftCard" />
            </div>
            <template v-if="allInclusivePricing">
                <div
                    v-if="showPricePerNight"
                    class="gst-price-details__price">
                    <span
                        v-if="showBasePriceWithoutDiscount"
                        class="gst-price-details__discount"
                        :class="{
                            'gst-price-details__redeem': redeemPointsToggle,
                        }">
                        {{ roundedUpBasePriceWithoutDiscountFormatted }}
                    </span>
                    <span
                        class="gst-price-details__price"
                        :class="{
                            'gst-price-details__price-redeem': redeemPointsToggle,
                        }">
                        {{ redeemPointsToggle ? pointsWithCount : roundedUpBasePriceFormatted }}
                        <span class="gst-price-details__price-primary--font-bold">
                            {{ $t('total') }}
                        </span>
                        <span
                            class="gst-price-details__price-primary--font-light"
                            :class="{
                                'gst-price-details__price-primary d-block': showTaxOnNewline,
                            }">
                            ({{ $t('taxFeesIncluded') }})
                        </span>
                    </span>

                    <!-- Fallback -->
                    <span
                        v-if="!showPricePerNight"
                        class="gst-price-details__price">
                        <span
                            v-if="!showBasePriceWithoutDiscount"
                            class="text-lowercase"
                            >{{ $t('_common:terms.from') }}</span
                        >
                        {{ roundedUpBasePriceFormatted }}
                    </span>
                </div>
                <span class="gst-price-details__price-secondary">
                    {{
                        redeemPointsToggle
                            ? pointsPerNightWithCount
                            : roundedUpPricePerNightFormatted
                    }}
                    {{ $t('perNight') }}
                </span>
            </template>
            <template v-else>
                <div
                    v-if="showPricePerNight"
                    class="gst-price-details__price">
                    {{ roundedUpPricePerNightFormatted }} {{ $t('perNight') }}
                </div>
                <span
                    v-if="showBasePriceWithoutDiscount"
                    class="gst-price-details__discount"
                    :class="
                        showPricePerNight
                            ? 'gst-price-details__discount--font-xxs'
                            : 'gst-price-details__discount--font-s'
                    ">
                    {{ roundedUpBasePriceWithoutDiscountFormatted }}
                </span>
                <span
                    v-if="showPricePerNight"
                    class="gst-price-details__price-secondary">
                    {{ roundedUpBasePriceFormatted }} {{ $t('total') }}
                    <span class="gst-price-details__price-secondary--font-light">
                        ({{ $t('taxFeesIncluded') }})
                    </span>
                </span>

                <!-- Fallback -->
                <span
                    v-if="!showPricePerNight"
                    class="gst-price-details__price">
                    <span
                        v-if="!showBasePriceWithoutDiscount"
                        class="text-lowercase">
                        {{ $t('_common:terms.from') }}
                    </span>
                    {{ roundedUpBasePriceFormatted }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import currencyFilterShort from '@core/filters/currencyShortNoDigits';
import SaveAmountCardChip from '@tenant/app/components/chips/SaveAmountCardChip';
import GiftCardChip from '@tenant/app/components/chips/GiftCardChip';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';

export default {
    name: 'PriceDetail',
    components: {
        SaveAmountCardChip,
        GiftCardChip,
    },
    mixins: [EventFlowMixin],
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel._components.priceDetail',
    },
    props: {
        giftCard: {
            type: Object,
            default: null,
        },
        // TODO Temporary added: HotelsListCard/HotelSelectedCard flow has GiftCardChip & SaveAmountCardChip separate,
        //until we have the designs corresponding to PriceDetail, we pass discountedAmount only at RoomOffer
        discountedAmount: {
            type: Number,
            default: 0,
        },
        // TODO To be used in future: Not used yet, will be replace when above TO DO is done for HotelsListCard
        hasMaxGiftCard: {
            type: Boolean,
            default: false,
        },
        totalPrices: {
            type: Object,
            required: true,
            default: () => ({}),
        },
        pricePerNight: {
            type: Number,
            default: null,
        },
        currency: {
            type: String,
            required: true,
            default: '',
        },
        allInclusivePricing: {
            type: Boolean,
            default: false,
        },
        showTaxOnNewline: {
            type: Boolean,
            default: false,
        },
        points: {
            type: Number,
            default: 0,
        },
        pointsPerNight: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            bundleProducts: CreateBundleProductsModel(),
        };
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
        }),
        showBasePriceWithoutDiscount() {
            return this.totalPrices.basePrice !== this.totalPrices.basePriceWithoutDiscount;
        },
        showDiscount() {
            return this.showBasePriceWithoutDiscount && this.discountedAmount;
        },
        showPricePerNight() {
            return this.isOpenInventoryFlow || this.isDynamicFlow;
        },
        roundedUpPricePerNightFormatted() {
            return this.$options.filters.currencyFilter(
                Math.ceil(this.pricePerNight),
                this.currency,
                { digits: 0 }
            );
        },
        roundedUpBasePriceFormatted() {
            return this.$options.filters.currencyFilter(
                Math.ceil(this.totalPrices.basePrice),
                this.currency,
                { digits: 0 }
            );
        },
        roundedUpBasePriceWithoutDiscountFormatted() {
            const value = Math.ceil(this.totalPrices.basePriceWithoutDiscount);
            return this.allInclusivePricing
                ? currencyFilterShort(value, this.currency, { digits: 0 })
                : this.$options.filters.currencyFilter(value, this.currency, { digits: 0 });
        },
        formattedPoints() {
            return this.$options.filters.bonusPoints(this.points);
        },
        pointsWithCount() {
            return this.$t('shared:common.pointWithCount', {
                count: this.points,
                formattedCount: this.formattedPoints,
            });
        },
        pointsPerNightWithCount() {
            return this.$t('shared:common.pointWithCount', {
                count: this.pointsPerNight,
                formattedCount: this.$options.filters.bonusPoints(this.pointsPerNight),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-price-details {
    text-align: left;

    .gst-price-details__gifts {
        gap: theme-spacing(1);
    }

    .gst-price-details__prices {
        @include mobile-only {
            text-align: left;
        }

        .gst-price-details__price {
            line-height: line-height('xxxl');
            color: theme-color('quaternary');
            font-size: font-size('l');
            font-weight: font-weight('large');

            &.gst-price-details__price-redeem {
                color: theme-color('primary');
            }
        }

        .gst-price-details__price-secondary {
            line-height: line-height('s');
            color: theme-color('tertiary');
            font-size: font-size('xxs');
            font-weight: font-weight('large');
        }

        .gst-price-details__price-secondary--font-light {
            font-weight: font-weight('light');
        }

        .gst-price-details__discount {
            margin-right: theme-spacing(1);
            color: theme-color('tertiary');
            text-decoration: line-through;
        }

        .gst-price-details__discount--font-s {
            line-height: line-height('xxxl');
            font-size: font-size('s');
        }

        .gst-price-details__discount--font-xxs {
            line-height: line-height('s');
            font-size: font-size('xxs');
        }
    }

    .gst-price-details__prices--all-inclusive {
        .gst-price-details__discount {
            line-height: line-height('xxxl');
            color: theme-color('quaternary');
            font-size: font-size('xxs');
            font-weight: font-weight('regular');
        }

        .gst-price-details__price-primary--font-bold {
            font-size: font-size('xxs');
            font-weight: font-weight('large');
        }

        .gst-price-details__price-primary--font-light {
            font-size: font-size('xxs');
            font-weight: font-weight('regular');
        }
    }
}
</style>
